import React, {useState, useEffect} from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  InputLabel,
  FormControl,
  Grid,
  TextField
} from "@mui/material";
import {inventoryCorrectionReasons} from "../constants/securspace-constants";
import {shouldUseCheckOutActivity} from "../../views/inventory-correction/util";
import {formatDateMMDDYYYY} from "../../util/DateUtils";
import DateCorrectionForm from "./forms/DateCorrectionForm";
import AssetNumberCorrectionForm from "./forms/AssetNumberCorrectionForm";
import moment from "moment-timezone";
import ReasonSelect from "./subComponents/ReasonSelect";
import type {GroupedLocationInventoryActivity} from "../../types/GroupedLocationInventoryActivity";
import {removeInitialSpecialChars} from "../../util/sanitizeUtil";
import {validateScacCode} from "../../util/ValidationUtils";
import {Moment} from "moment-timezone";
import {useSnackbar} from "../../context/snackbar-context";

interface CorrectionRequestBody {
  locationInventoryActivityId: string;
  reason: string;
  rejectionReason: string;
  chassisNumber: string | null;
  containerNumber: string | null;
  trailerNumber: string | null;
  truckLicensePlateNumber: string | null;
  scacCode?: string | null;
  driverFirstName: string;
  driverLastName: string;
  driverLicenseNumber: string;
  notes: string;
  correctActivityDate?: string;
}

interface AssetInfo {
  containerNumber?: string | null;
  chassisNumber?: string | null;
  trailerNumber?: string | null;
  truckLicensePlateNumber?: string | null;
}

interface CurrentTimeData {
  dateTime: Moment | string;
  timeZoneName: string;
  timeZoneAbbreviation: string;
}

interface ErrorMessages {
  scacCode: string;
}

interface SubmitCorrectionDialogProps {
  onClose: () => void;
  inventory: GroupedLocationInventoryActivity;
  submitCorrection: (body: CorrectionRequestBody) => void;
}

const SubmitCorrectionDialog = (props: SubmitCorrectionDialogProps) => {

  const {onClose, inventory, submitCorrection} = props;
  const { snackbarShowMessage } = useSnackbar();

  const defaultAsset = {
    containerNumber: '',
    chassisNumber: '',
    trailerNumber: '',
    truckLicensePlateNumber: ''
  };
  const defaultDate = inventory?.checkoutDate;

  const [reason, setReason] = useState<typeof inventoryCorrectionReasons[keyof typeof inventoryCorrectionReasons] | ''>('');

  const [driverFirst, setDriverFirst] = useState('');
  const [driverLast, setDriverLast] = useState('');
  const [driverLicense, setDriverLicense] = useState('');

  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
  const [correctActivityDate, setCorrectActivityDate] = useState<string | undefined>(defaultDate);
  const [correctAssetInfo, setCorrectAssetInfo] = useState<AssetInfo>(defaultAsset);
  const [currentTimeData, setCurrentTimeData] = useState<CurrentTimeData>({
    dateTime: "",
    timeZoneName: "",
    timeZoneAbbreviation: ""
  })
  const [notes, setNotes] = useState<string>('');
  const [scacCode, setScacCode] = useState<string>(inventory?.scacCode || '');
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({
    scacCode: ''
  });

  const dateCorrectionReasons: Array<typeof inventoryCorrectionReasons.ASSET_LEFT_THE_YARD | typeof inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE> = [inventoryCorrectionReasons.ASSET_LEFT_THE_YARD, inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE];

  useEffect(() => {
    if (inventory) {
      const {
        checkinDriverFirstName,
        checkinDriverLastName,
        checkinDriverLicenseNumber,
        checkoutDriverFirstName,
        checkoutDriverLastName,
        checkoutDriverLicenseNumber
      } = inventory;

      const useCheckOutData = shouldUseCheckOutActivity(inventory, reason) || reason === inventoryCorrectionReasons.ASSET_LEFT_THE_YARD;

      setScacCode(inventory.scacCode || '')
      setDriverFirst(useCheckOutData ? checkoutDriverFirstName || '' : checkinDriverFirstName || '');
      setDriverLast(useCheckOutData ? checkoutDriverLastName || '' : checkinDriverLastName || '');
      setDriverLicense(useCheckOutData ? checkoutDriverLicenseNumber || '' : checkinDriverLicenseNumber || '');
    }

  }, [reason, inventory]);

  const handleChangeReason = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement> | {
    target: { value: string; name: string }
  }) => {
    setReason(e.target.value as typeof reason);
  };

  const handleClose = () => {
    setReason('');
    setCorrectActivityDate('');
    setCorrectAssetInfo(defaultAsset);
    setScacCode('');
    setNotes('');
    setDisableSubmit(true)
    onClose();
  };

  const handleChangeTextField = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeInitialSpecialChars(target.value);
    switch (target.name) {
      case "notes":
        setNotes(value);
        break;
      case "scacCode":
        setScacCode(value.toUpperCase());
        setErrorMessages({
          ...errorMessages,
          scacCode: !value || validateScacCode(value) ? '' : 'SCAC code must be alphanumeric and be 2 to 4 characters long.'
        })
        break;
      default:
        break;
    }
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (reason && inventory) {
      let checkInId: string | undefined, checkOutId: string | undefined;
      if (Array.isArray(inventory.ids)) {
        // this is when the modal is opened from the BuyerInventoryCorrections page (when inventory.ids array is present)
        [checkInId, checkOutId] = inventory.ids;
      } else {
        // @ts-expect-error - checkInId exists when modal opened from Daily Activity Page
        checkInId = inventory.checkInId;
        // @ts-expect-error - checkOutId exists when modal opened from Daily Activity Page
        checkOutId = inventory.checkOutId;
      }
      const activityId = shouldUseCheckOutActivity(inventory, reason) ? checkOutId : checkInId;

      if (!activityId) {
        snackbarShowMessage("Unable to determine activity ID", "error");
        return;
      }

      const equipment = {
        CONTAINER_NUMBER: correctAssetInfo.containerNumber ? correctAssetInfo.containerNumber : inventory.containerNumber,
        CHASSIS_NUMBER: correctAssetInfo.chassisNumber ? correctAssetInfo.chassisNumber : inventory.chassisNumber,
        TRUCK_NUMBER: correctAssetInfo.truckLicensePlateNumber ? correctAssetInfo.truckLicensePlateNumber : inventory.truckLicensePlateNumber,
        TRAILER_NUMBER: correctAssetInfo.trailerNumber ? correctAssetInfo.trailerNumber : inventory.trailerNumber
      };

      const body: CorrectionRequestBody = {
        locationInventoryActivityId: activityId,
        reason: reason,
        rejectionReason: '',
        chassisNumber: equipment.CHASSIS_NUMBER,
        containerNumber: equipment.CONTAINER_NUMBER,
        trailerNumber: equipment.TRAILER_NUMBER,
        truckLicensePlateNumber: equipment.TRUCK_NUMBER,
        scacCode: scacCode ? scacCode : inventory.scacCode,
        driverFirstName: driverFirst,
        driverLastName: driverLast,
        driverLicenseNumber: driverLicense,
        notes: notes
      };

      if ((reason === inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE || reason === inventoryCorrectionReasons.ASSET_LEFT_THE_YARD) && correctActivityDate) {
        if (moment(correctActivityDate).isAfter(currentTimeData.dateTime)) {
          snackbarShowMessage(`Please select an activity time before ${moment(currentTimeData.dateTime).format("hh:mm A MM/DD/YY")} ${currentTimeData.timeZoneAbbreviation}`, 'error', 4000)
        } else {
          body.correctActivityDate = moment(correctActivityDate).toISOString(true)
          submitCorrection(body);
          handleClose();
        }
      } else {
        submitCorrection(body);
        handleClose();
      }

    } else {
      snackbarShowMessage("Please select a reason in order to submit a correction for review.", "warning");
    }
  }

  useEffect(() => {
    if (inventory?.timeZoneName) {
      const time = moment.tz(moment(), inventory.timeZoneName);
      const abbrev = moment().tz(inventory.timeZoneName).format('z');
      setCurrentTimeData({dateTime: time, timeZoneName: inventory.timeZoneName, timeZoneAbbreviation: abbrev});
    }
  }, [inventory])

  useEffect(() => {
    if (inventory && !correctActivityDate) {
      setCorrectActivityDate(inventory.checkoutDate)
    }
    if (inventory && !correctAssetInfo?.containerNumber && !correctAssetInfo?.chassisNumber && !correctAssetInfo?.trailerNumber && !correctAssetInfo?.truckLicensePlateNumber) {
      const defaultAsset = {
        containerNumber: inventory.containerNumber,
        chassisNumber: inventory.chassisNumber,
        trailerNumber: inventory.trailerNumber,
        truckLicensePlateNumber: inventory.truckLicensePlateNumber
      };
      setCorrectAssetInfo(defaultAsset)
    }
  }, [correctActivityDate, correctAssetInfo, inventory])

  useEffect(() => {
    const driverIsNotValid = !driverFirst || !driverLast || !driverLicense;
    const scacCodeIsValid = !errorMessages.scacCode;
    const scacCodeEdited = scacCode !== (inventory?.scacCode || '');

    const assetNumbersMatch = () => {
      const getValueOrEmptyString = (value: string | null | undefined): string => {
        return value === undefined || value === null ? "" : value.toLowerCase();
      }

      return (correctAssetInfo && inventory) && Object.keys(correctAssetInfo).every(key => {
        const typedKey = key as keyof AssetInfo;
        return getValueOrEmptyString(inventory[typedKey]) === getValueOrEmptyString(correctAssetInfo[typedKey]);
      });
    }

    switch (reason) {
      case inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE:
        if (formatDateMMDDYYYY(correctActivityDate) === formatDateMMDDYYYY(inventory.checkoutDate) || driverIsNotValid || moment(correctActivityDate).isAfter(currentTimeData.dateTime)) {
          setDisableSubmit(true)
        } else {
          setDisableSubmit(false)
        }
        break;
      case inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY:
        setDisableSubmit(!(scacCodeEdited || !assetNumbersMatch()) || driverIsNotValid || !scacCodeIsValid);
        break;
      case inventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER:
        setDisableSubmit(false)
        break;
      case inventoryCorrectionReasons.ASSET_LEFT_THE_YARD:
        setDisableSubmit(driverIsNotValid || !moment(correctActivityDate).isValid() || !correctAssetInfo.truckLicensePlateNumber || moment(correctActivityDate).isAfter(currentTimeData.dateTime))
        break;
      default:
        setDisableSubmit(true)
        break
    }
  }, [reason, correctActivityDate, correctAssetInfo, driverFirst, driverLast, driverLicense, inventory, currentTimeData, scacCode, errorMessages]);

  return <Dialog open={!!inventory} onClose={handleClose} fullWidth scroll={"body"}>
    <DialogTitle>Correction Reasons</DialogTitle>
    <DialogContent sx={{paddingTop: "1em !important"}}>
      <form id={'ss-correction-form'} onSubmit={handleSubmit} noValidate>
        <Grid container gap={2}>
          <Grid item xs={12}>
            <ReasonSelect value={reason}
                          onChange={handleChangeReason}
                          groupedLocationInventoryActivity={inventory}/>
          </Grid>
          {
            dateCorrectionReasons.includes(reason as typeof dateCorrectionReasons[number]) ?
              <DateCorrectionForm
                inventory={inventory}
                correctActivityDate={correctActivityDate ? moment(correctActivityDate) : null}
                setCorrectActivityDate={(date) => setCorrectActivityDate(date ? date.toISOString() : undefined)}
                currentTimeData={{
                  dateTime: typeof currentTimeData.dateTime === 'string' ? moment(currentTimeData.dateTime) : currentTimeData.dateTime,
                  timeZoneName: currentTimeData.timeZoneName,
                  timeZoneAbbreviation: currentTimeData.timeZoneAbbreviation
                }}
                disableDatesBeforeCheckIn={true}
              />
              : null
          }
          {
            reason && reason !== inventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER ?
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="ss-select-correction-scac-code"
                    value={scacCode}
                    error={!!errorMessages.scacCode}
                    helperText={errorMessages.scacCode ? errorMessages.scacCode : ''}
                    type="text"
                    name="scacCode"
                    onChange={handleChangeTextField}
                    label="SCAC Code"
                  />
                </FormControl>
                <AssetNumberCorrectionForm
                  inventory={inventory}
                  setCorrectAssetInfo={setCorrectAssetInfo}
                  driverFirst={driverFirst}
                  driverLast={driverLast}
                  driverLicense={driverLicense}
                  setDriverFirst={setDriverFirst}
                  setDriverLast={setDriverLast}
                  setDriverLicense={setDriverLicense}
                />
              </Grid>
              : null
          }
          <Grid item xs={12}>
            <InputLabel htmlFor="ss-select-correction-notes">Notes (optional)</InputLabel>
            <FormControl fullWidth>
              <TextField
                id="ss-select-correction-notes"
                value={notes}
                name="notes"
                type="text"
                onChange={handleChangeTextField}
              />
            </FormControl>
          </Grid>
        </Grid>
      </form>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleClose}>Cancel</Button>
      <Button form={'ss-correction-form'}
              disabled={disableSubmit}
              type={'submit'}>Submit</Button>
    </DialogActions>
  </Dialog>
}

export default SubmitCorrectionDialog;
